import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth as authApi } from '@/utils/api';
import { ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch {
      // Intentionally still show "sent" to avoid leaking which emails exist
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }} data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm mb-6" style={{ color: 'var(--muted)' }}>
          <ArrowLeft size={14} /> Back to sign in
        </Link>
        <div className="card-base p-6">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-sent-state">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#3C8A5A15' }}>
                <CheckCircle2 size={22} style={{ color: '#3C8A5A' }} />
              </div>
              <h1 className="text-xl font-black mb-1.5" style={{ color: 'var(--ink)' }}>Check your email</h1>
              <p className="text-sm mb-4" style={{ color: 'var(--muted)' }}>
                If an account exists for <strong>{email}</strong>, we've sent a reset link. It expires in 1 hour.
              </p>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                Didn't receive it? Check spam, or verify the email address is correct.
              </p>
              <button onClick={() => { setSent(false); setEmail(''); }} className="mt-4 text-sm font-semibold" style={{ color: '#006080' }}>
                Try another email
              </button>
            </div>
          ) : (
            <>
              <div className="w-10 h-10 rounded-btn flex items-center justify-center mb-3" style={{ background: '#E6F1F4' }}>
                <Mail size={16} style={{ color: '#006080' }} />
              </div>
              <h1 className="text-xl font-black mb-1" style={{ color: 'var(--ink)' }}>Forgot your password?</h1>
              <p className="text-sm mb-5" style={{ color: 'var(--muted)' }}>
                Enter the email you use to sign in. We'll email you a link to set a new password.
              </p>
              <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
                <div>
                  <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Email</label>
                  <input
                    className="input-agency"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full py-2.5 text-sm"
                  disabled={loading || !email.trim()}
                  data-testid="forgot-submit"
                >
                  {loading ? 'Sending…' : 'Send reset link'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
